/* ------------------------ Functionsl imports start ------------------------ */
import { Chip, Stack } from '@mui/material'
import { type ColumnProps } from 'antd/es/table/Column'
import { useTranslation } from 'react-i18next'
import React from 'react'
import { Link } from 'react-router-dom'

import { cubic_symbol, formatMeasurement } from './functions'
import { type Article } from './types'
import Button from '../../../components/Button'
import { type APICategory } from '../../../generated-types/models/APICategory'
import { type APIProperty } from '../../../generated-types/models/APIProperty'
import { useUserContext } from '../../../utils/context'
import {
  canCreate,
  isPrivileged,
  renderDateTime,
} from '../../../utils/functions'
import { type SelectOption, type SmartTableColumnType } from '../../../utils/types'
import { type FormFieldType } from '../../Form'
import { PERMISSIONS } from '../../../config/permissions'
import { type Request } from '../../Request/utils/types'


/* ------------------------- Functional imports end ------------------------- */

type Props = {
  name:
    | 'articleTableColumns'
    | 'generalFormFields'
    | 'dimensionsFormFields'
    | 'purchasedPartFormFields'
    | 'manufacturingProcessFormFields'
    | 'assemblyFormFields'
    | 'joiningFormFields'
    | 'cncMachiningFormFields'
    | 'castingFormFields'
    | 'postProcessingFormFields'
    | 'sheetMetalFormFields'
    | 'additiveManufacturingFormFields'
    | 'standardComponentFormFields'
    | 'rawMaterialFormFields'
    | 'qualityControlFormFields'
    | 'customProcessFormFields'
    | 'wareHouseFormFields'
    | 'propertyFormFields'
    | 'categoryFormFields'
    | 'categoryList'
    | 'manufacturingProcessOptions'
    | 'sharepointFormFields'
    | 'editSharepointFormFields'
} & (
  | ArticleTableKwargs
  | GeneralKwargs
  | DimensionKwargs
  | PurchasedPartKwargs
  | ManufacturingProcessKwargs
  | AssemblyKwargs
  | JoiningKwargs
  | CNCMachiningKwargs
  | CastingKwargs
  | PostProcessingKwargs
  | SheetMetalKwargs
  | AdditiveManufacturingKwargs
  | StandardComponentKwargs
  | RawMaterialKwargs
  | QualityControlKwargs
  | CustomProcessKwargs
  | WarehouseKwargs
  | PropertyKwargs
  | CategoryKwargs
  | CategoryListKwargs
  | ManufacturingProcessOptionsKwargs
  | SharepointFormFieldsKwargs
  | EditSharepointFormFieldsKwargs
)

type ArticleTableKwargs = {
  name: 'articleTableColumns'
  handleProvideFeedback: (e: Event, selectedArticle: Article) => void
  hideProvideFeedbackButton: boolean
  showFeedbackStatus?: boolean
  isFeedbackSubmitted?: (record: Article) => boolean
  isStaffUser?: boolean
  categoryMap?: Map<string, any>
  propertyMap?: Map<string, any>
  typeMap?: Map<string, any>
  familyMap?: Map<string, any>
  supplierMap?: Map<string, any>
  articleMap?: Map<string, Article>
}
type GeneralKwargs = {
  name: 'generalFormFields'
  brandOptions: SelectOption[]
  articleOptions: SelectOption[]
  categoryOptions: SelectOption[]
  propertyOptions: SelectOption[]
  onAddPropertyOption: () => void
  onAddCategoryOption: () => void
}

type PropertyKwargs = {
  name: 'propertyFormFields'
}

type CategoryKwargs = {
  name: 'categoryFormFields'
}

type DimensionKwargs = {
  name: 'dimensionsFormFields'
  isRequiredDimension: (dimension: string) => boolean
}
type PurchasedPartKwargs = {
  name: 'purchasedPartFormFields'
  supplierOptions: SelectOption[]
  onAddSupplierOption: () => void
}

type ManufacturingProcessKwargs = {
  name: 'manufacturingProcessFormFields'
  options: { value: string; label: string }[]
}

type AssemblyKwargs = {
  name: 'assemblyFormFields'
}
type JoiningKwargs = {
  name: 'joiningFormFields'
}
type CNCMachiningKwargs = {
  name: 'cncMachiningFormFields'
}
type CastingKwargs = {
  name: 'castingFormFields'
}
type PostProcessingKwargs = {
  name: 'postProcessingFormFields'
}
type SheetMetalKwargs = {
  name: 'sheetMetalFormFields'
}
type AdditiveManufacturingKwargs = {
  name: 'additiveManufacturingFormFields'
}
type StandardComponentKwargs = {
  name: 'standardComponentFormFields'
}
type RawMaterialKwargs = {
  name: 'rawMaterialFormFields'
}
type QualityControlKwargs = {
  name: 'qualityControlFormFields'
}
type CustomProcessKwargs = {
  name: 'customProcessFormFields'
}
type WarehouseKwargs = {
  name: 'wareHouseFormFields'
}
type CategoryListKwargs = {
  name: 'categoryList'
}
type ManufacturingProcessOptionsKwargs = {
  name: 'manufacturingProcessOptions'
}
type SharepointFormFieldsKwargs = {
  name: 'sharepointFormFields'
  feedbackFormOptions?: SelectOption[]
  onAddFeedbackFormOption?: () => void
  ArticlesListComponent?: React.ReactElement
  ArticlesDescriptionComponent?: React.ReactElement
  DurationInputComponent?: React.ReactElement
  DurationDescriptionComponent?: React.ReactElement
  t?: (key: string) => string
}
type EditSharepointFormFieldsKwargs = {
  name: 'editSharepointFormFields'
  ArticlesListComponent?: React.ReactElement
  ArticlesDescriptionComponent?: React.ReactElement
  RemainingDurationInputComponent?: React.ReactElement
  DurationDescriptionComponent?: React.ReactElement
  t?: (key: string) => string
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */

export default function GetConstant(props: Props): FormFieldType[] | ColumnProps<Article>[] {
  /* -------------------------- Non state data start -------------------------- */
  const { t: tHook } = useTranslation()
  const { userPermissions, user } = useUserContext()
  // Use provided t function or fallback to hook for sharepoint form fields
  const t = (props.name === 'sharepointFormFields' || props.name === 'editSharepointFormFields')
    ? ((props as SharepointFormFieldsKwargs | EditSharepointFormFieldsKwargs).t || tHook)
    : tHook
  /* --------------------------- Non state data end --------------------------- */

  if (props.name === 'articleTableColumns') {
    return [
      {
        title: t('common:content.label.name'),
        dataIndex: 'name',
        key: 'name',
        render: (name: any) => name ?? '-',
        sorter: true,
        filter: {
          inputType: 'text',
          groupBy: {
            key: 'articleInformation',
            label: t('item:content.label.articleInformation')
          },
        },
      },
      {
        title: t('common:content.label.articleNumber'),
        dataIndex: 'number',
        key: 'number',
        render: (number: any) => number ?? '-',
        sorter: true,
        filter: {
          inputType: 'text',
          groupBy: {
            key: 'articleInformation',
            label: t('item:content.label.articleInformation')
          },
        },
      },
      ...((props as ArticleTableKwargs).showFeedbackStatus ? [{
        title: t('sharepoint:content.label.feedbackStatus'),
        dataIndex: 'feedbackStatus',
        key: 'feedbackStatus',
        render: (_: unknown, record: Article) => {
          const hasFeedback = (props as ArticleTableKwargs).isFeedbackSubmitted?.(record) ?? false
          const statusLabel = hasFeedback
            ? t('item:content.label.feedbackAvailable')
            : t('item:content.label.feedbackMissing')

          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <span
                title={statusLabel}
                style={{
                  display: 'inline-block',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  minWidth: '10px',
                  backgroundColor: hasFeedback ? '#2e7d32' : '#d32f2f',
                }}
              />
              <span>{statusLabel}</span>
            </Stack>
          )
        },
      }] : []),
      {
        title: t('common:content.label.weight'),
        dataIndex: 'weight',
        key: 'weight',
        render: (weight: any) => formatMeasurement(weight?.value, weight?.unit),
        filter: {
          inputType: 'number',
          groupBy: {
            key: 'dimensions',
            label: t('common:content.label.dimensions')
          },
        },
      },
      {
        title: t('common:content.label.length'),
        dataIndex: 'length',
        key: 'length',
        render: (measurement: any) => formatMeasurement(measurement?.value, measurement?.unit),
        filter: {
          inputType: 'number',
          groupBy: {
            key: 'dimensions',
            label: t('common:content.label.dimensions')
          },
        },
      },
      {
        title: t('common:content.label.width'),
        dataIndex: 'width',
        key: 'width',
        render: (measurement: any) => formatMeasurement(measurement?.value, measurement?.unit),
        filter: {
          inputType: 'number',
          groupBy: {
            key: 'dimensions',
            label: t('common:content.label.dimensions')
          },
        },
      },
      {
        title: t('common:content.label.height'),
        dataIndex: 'height',
        key: 'height',
        render: (measurement: any) => formatMeasurement(measurement?.value, measurement?.unit),
        filter: {
          inputType: 'number',
          groupBy: {
            key: 'dimensions',
            label: t('common:content.label.dimensions')
          },
        },
      },
      {
        title: t('common:content.label.volume'),
        dataIndex: 'volume',
        key: 'volume',
        render: (measurement: any) => formatMeasurement(measurement?.value, measurement?.unit),
        filter: {
          inputType: 'number',
          groupBy: {
            key: 'dimensions',
            label: t('common:content.label.dimensions')
          },
        },
      },
      {
        title: t('common:content.label.type'),
        dataIndex: 'type',
        key: 'type',
        render: (type: any) => {
          if (!type) return '-'
          
          // If type is a URL (string), use the map to resolve it
          if (typeof type === 'string') {
            // Check if it's a URL (contains http or is a relative URL starting with /)
            if (type.startsWith('http') || type.startsWith('/')) {
              const typeMap = (props as ArticleTableKwargs).typeMap
              if (!typeMap) return '-'
              const typeObj = typeMap.get(type)
              return typeObj ? typeObj.name : '-'
            }
            // If it's just a plain string value, display it directly
            return type
          }
          
          // If type is a full object (backward compatibility)
          return type?.name || '-'
        },
        filter: {
          inputType: 'text',
          groupBy: {
            key: 'articleInformation',
            label: t('item:content.label.articleInformation')
          },
        },
      },
      {
        title: t('common:content.label.family'),
        dataIndex: 'family',
        key: 'family',
        render: (family: any) => {
          if (!family) return '-'
          
          // If family is a URL (string), use the map to resolve it
          if (typeof family === 'string') {
            const familyMap = (props as ArticleTableKwargs).familyMap
            if (!familyMap) return '-'
            const familyObj = familyMap.get(family)
            return familyObj ? familyObj.name : '-'
          }
          
          // If family is a full object (backward compatibility)
          return family?.name || '-'
        },
        filter: {
          inputType: 'text',
          groupBy: {
            key: 'articleInformation',
            label: t('item:content.label.articleInformation')
          },
        },
      },
      {
        title: t('common:content.label.category'),
        dataIndex: 'category',
        key: 'category',
        render: (categoryList: any) => {
          if (!Array.isArray(categoryList)) return '-'
          if (categoryList.length === 0) return '-'
          
          // If categoryList contains URLs (strings), use the map to resolve them
          if (typeof categoryList[0] === 'string') {
            const categoryMap = (props as ArticleTableKwargs).categoryMap
            if (!categoryMap) return '-'
            
            return (
              <Stack direction="row" spacing={1}>
                {(categoryList as string[]).map((url: string) => {
                  const category = categoryMap.get(url)
                  return category ? (
                    <Chip label={category.name} key={url} />
                  ) : null
                })}
              </Stack>
            )
          }
          
          // If categoryList contains full objects (backward compatibility)
          return (
            <Stack direction="row" spacing={1}>
              {(categoryList as APICategory[]).map((category: APICategory) => {
                return <Chip label={category.name} key={category.self} />
              })}
            </Stack>
          )
        },
        filter: {
          inputType: 'text',
          groupBy: {
            key: 'categoriesProperties',
            label: t('common:content.label.categoriesProperties')
          },
        },
      },
      {
        title: t('common:content.label.variationOf'),
        dataIndex: 'variationOf',
        key: 'variationOf',
        render: (variationOf: any) => {
          if (!variationOf) return '-'
          
          // If variationOf is a URL (string), try to resolve it via articleMap
          if (typeof variationOf === 'string') {
            // Check if it's a URL (contains http or is a relative URL starting with /)
            if (variationOf.startsWith('http') || variationOf.startsWith('/')) {
              const articleMap = (props as ArticleTableKwargs).articleMap
              if (!articleMap) return '-'
              const articleObj = articleMap.get(variationOf)
              if (articleObj) {
                // Found in map - show name and number
                const name = articleObj.name || ''
                const number = articleObj.number || ''
                return name ? `${name} (${number})` : number || '-'
              }
              // Not found in map - extract and show article ID from URL
              const match = variationOf.match(/articles\/(\d+)/)
              return match ? `Article #${match[1]}` : '-'
            }
            // If it's just a plain string value, display it directly
            return variationOf
          }
          
          // If variationOf is a full article object (backward compatibility)
          if (variationOf?.name || variationOf?.number) {
            const name = variationOf.name || ''
            const number = variationOf.number || ''
            return name ? `${name} (${number})` : number || '-'
          }
          
          return '-'
        },
        filter: {
          inputType: 'text',
          groupBy: {
            key: 'articleInformation',
            label: t('item:content.label.articleInformation')
          },
        },
      },
      {
        title: t('common:content.label.properties'),
        dataIndex: 'properties',
        key: 'properties',
        render: (propertyList: any) => {
          if (!Array.isArray(propertyList)) return '-'
          if (propertyList.length === 0) return '-'
          
          // Helper function to translate AQG upload property names
          const translatePropertyName = (propertyName: string): string => {
            const treatmentTranslation = t('item:content.label.post_treatment')
            const translationMap: Record<string, string> = {
              'manufacturing_process': t('common:content.label.manufacturingProcess'),
              'treatment': treatmentTranslation === 'item:content.label.post_treatment' ? 'Treatment' : treatmentTranslation,
            }
            return translationMap[propertyName] || propertyName
          }
          
          // If propertyList contains URLs (strings), use the map to resolve them
          if (typeof propertyList[0] === 'string') {
            const propertyMap = (props as ArticleTableKwargs).propertyMap
            if (!propertyMap) return '-'
            
            return (
              <Stack direction="row" spacing={1}>
                {(propertyList as string[]).map((url: string) => {
                  const property = propertyMap.get(url)
                  return property ? (
                    <Chip label={`${translatePropertyName(property.name)}: ${property.value}`} key={url} />
                  ) : null
                })}
              </Stack>
            )
          }
          
          // If propertyList contains full objects (backward compatibility)
          return (
            <Stack direction="row" spacing={1}>
              {(propertyList as APIProperty[]).map((property: APIProperty, index: number) => {
                return <Chip label={`${translatePropertyName(property.name)}: ${property.value}`} key={index} />
              })}
            </Stack>
          )
        },
        filter: {
          inputType: 'text',
          groupBy: {
            key: 'categoriesProperties',
            label: t('common:content.label.categoriesProperties')
          },
        },
      },
      {
        title: t('common:content.label.createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (date: any) => renderDateTime(date, { showTime: true }),
        sorter: true,
        filter: {
          inputType: 'date',
          groupBy: {
            key: 'other',
            label: t('common:content.label.other')
          },
        },
      },
      {
        title: `${t('common:content.label.brand')} / ${t('common:content.label.manufacturer')}`,
        dataIndex: 'brand',
        key: 'brand',
        render: (brand: any) => brand ?? '-',
        sorter: true,
        filter: {
          inputType: 'text',
          groupBy: {
            key: 'articleInformation',
            label: t('item:content.label.articleInformation')
          },
        },
      },
      {
        title: t('item:content.label.material'),
        dataIndex: 'material',
        key: 'material',
        render: (material: any) => {
          if (!material) return '-'
          
          // If material is a URL (string), try to resolve it
          if (typeof material === 'string') {
            // If it's just a plain string value, display it directly
            return material
          }
          
          // If material is a full object (backward compatibility)
          return material?.name || '-'
        },
        sorter: true,
        filter: {
          inputType: 'text',
          filterKey: 'material',
          groupBy: {
            key: 'articleInformation',
            label: t('item:content.label.articleInformation')
          },
        },
      },
      {
        title: t('common:content.label.barcode'),
        dataIndex: 'barcodeText',
        key: 'barcodeText',
        render: (barcodeText: any) => barcodeText ?? '-',
        filter: {
          inputType: 'text',
          groupBy: {
            key: 'barcode',
            label: t('common:content.label.barcode')
          },
        },
      },
      {
        title: t('common:content.label.barcodeType'),
        dataIndex: 'barcodeType',
        key: 'barcodeType',
        render: (barcodeType: any) => barcodeType ?? '-',
        filter: {
          inputType: 'text',
          groupBy: {
            key: 'barcode',
            label: t('common:content.label.barcode')
          },
        },
      },
      {
        title: t('common:content.label.lastModifiedAt'),
        dataIndex: 'lastModifiedAt',
        key: 'lastModifiedAt',
        render: (date: any) => renderDateTime(date, { showTime: true }),
        sorter: true,
        filter: {
          inputType: 'date',
          filterKey: 'last_modified_at',
          groupBy: {
            key: 'other',
            label: t('common:content.label.other')
          },
        },
      },
      // Staff-only column: Requests (clickable links)
      ...((props as ArticleTableKwargs).isStaffUser ? [{
        title: t('common:content.label.requests', 'Requests'),
        dataIndex: 'requests',
        key: 'requests',
        render: (requests: Request[] | string | undefined) => {
          if (!requests || typeof requests === 'string' || !Array.isArray(requests)) return '-'
          if (requests.length === 0) return '-'
          
          return (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {requests.slice(0, 3).map((request: Request) => (
                <Link 
                  key={request.key} 
                  to={{
                    pathname: '/contracts',
                    search: `?tab=requests&requestId=${request.key}`,
                  }}
                  style={{ textDecoration: 'none' }}
                >
                  <Chip 
                    label={`REQ-${request.key}`} 
                    size="small" 
                    clickable 
                    color="primary" 
                    variant="outlined"
                  />
                </Link>
              ))}
              {requests.length > 3 && (
                <Chip label={`+${requests.length - 3}`} size="small" />
              )}
            </Stack>
          )
        },
        filter: {
          inputType: 'text',
          filterKey: 'request_id',
          groupBy: {
            key: 'staffFilters',
            label: t('common:content.label.staffFilters', 'Staff Filters')
          },
        },
      }] : []),
      // Staff-only column: Organization (derived from requests)
      ...((props as ArticleTableKwargs).isStaffUser ? [{
        title: t('common:content.label.organization', 'Organization'),
        dataIndex: 'requests',
        key: 'organization',
        render: (requests: Request[] | string | undefined) => {
          if (!requests || typeof requests === 'string' || !Array.isArray(requests)) return '-'
          if (requests.length === 0) return '-'
          
          const orgName = requests[0]?.organizationName
          return orgName ?? '-'
        },
        filter: {
          inputType: 'text',
          filterKey: 'organization',
          groupBy: {
            key: 'staffFilters',
            label: t('common:content.label.staffFilters', 'Staff Filters')
          },
        },
      }] : []),
      {
        title: '',
        key: 'feedbackAction',
        dataIndex: 'feedbackAction',
        render: (text: string, record: any) => {
          if (props.hideProvideFeedbackButton) return <></>
          else {
            return (
              <Button
                id="provideArticleFeedbackButton"
                variant="contained"
                onClick={(e: Event) => props.handleProvideFeedback(e, record)}
              >
                {t('common:content.heading.giveFeedback')}
              </Button>
            )
          }
        },
      },
    ] as SmartTableColumnType<Article>[]
  }

  if (props.name === 'generalFormFields') {
    return [
      {
        type: 'text',
        key: 'name',
        label: t('common:content.label.articleName'),
        required: false,
      },
      {
        type: 'text',
        key: 'number',
        label: t('common:content.label.articleNumber'),
        required: true,
        validation: true,
      },
      {
        type: 'multiline',
        key: 'description',
        label: t('common:content.label.description'),
        xl: 12,
      },
      {
        type: 'text',
        key: 'material',
        label: t('item:content.label.material'),
        autocompleteField: 'materials',
        required: true,
        validation: true,
      },
      {
        type: 'text',
        key: 'barcodeText',
        label: t('common:content.label.barcode'),
      },
      {
        type: 'text',
        key: 'barcodeType',
        label: t('common:content.label.barcodeType'),
        autocompleteField: 'barcode-types',
        /* options: [
          { value: 'ean', label: 'EAN' },
          { value: 'gtin', label: 'GTIN' },
          { value: 'upc', label: 'UPC' },
          { value: 'custom', label: t('common:content.label.custom') as string},
        ], */
      },
      {
        type: 'text',
        key: 'type',
        label: t('common:content.label.type'),
        autocompleteField: 'article-types',
      },
      {
        type: 'text',
        key: 'family',
        label: t('common:content.label.family'),
        autocompleteField: 'families',
      },
      {
        type: 'select-search-multi',
        key: 'properties',
        label: t('common:content.label.properties'),
        autocompleteField: 'properties',
        required: false,
        validation: false,
        onAddOption:
          (canCreate(userPermissions, PERMISSIONS.ITEMS.PROPERTY) || isPrivileged(user)) &&
          props.onAddPropertyOption,
      },
      {
        type: 'select-search-multi',
        key: 'category',
        label: t('common:content.label.categories'),
        autocompleteField: 'categories',
        required: false,
        validation: false,
        onAddOption:
          (canCreate(userPermissions, PERMISSIONS.ITEMS.CATEGORY) || isPrivileged(user)) &&
          props.onAddCategoryOption,
      },
      {
        type: 'text',
        key: 'brand',
        label: t('common:content.label.brand'),
        autocompleteField: 'brands',
      },
      {
        type: 'select-search',
        key: 'variationOf',
        label: t('common:content.label.variationOf'),
        autocompleteField: 'articles',
        required: false,
        validation: false,
      },
      {
        type: 'select-search',
        key: 'qualityClass',
        label: t('common:content.label.qualityClass'),
        options: [
          { value: 'f', label: 'f' },
          { value: 'm', label: 'm' },
          { value: 'c', label: 'c' },
          { value: 'v', label: 'v' },
        ],
        required: false,
        validation: false,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'propertyFormFields') {
    return [
      {
        type: 'text',
        key: 'name',
        label: t('common:content.label.name'),
        required: true,
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
      {
        type: 'text',
        key: 'value',
        label: t('common:content.label.value'),
        required: true,
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
      {
        type: 'text',
        key: 'type',
        label: t('common:content.label.type'),
        required: true,
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'categoryFormFields') {
    return [
      {
        type: 'text',
        key: 'name',
        label: t('common:content.label.name'),
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'dimensionsFormFields') {
    // Metric unit options
    const weightUnitOptions = [
      { value: 'mg', label: 'mg' },
      { value: 'g', label: 'g' },
      { value: 'kg', label: 'kg' },
    ]
    const lengthUnitOptions = [
      { value: 'mm', label: 'mm' },
      { value: 'cm', label: 'cm' },
      { value: 'm', label: 'm' },
      { value: 'km', label: 'km' },
    ]
    const volumeUnitOptions = [
      { value: 'cubic_millimeter', label: 'mm³' },
      { value: 'cubic_centimeter', label: 'cm³' },
      { value: 'cubic_meter', label: 'm³' },
    ]

    return [
      {
        type: 'measurement',
        key: 'weight',
        label: t('common:content.label.weight'),
        errorMessage: t('item:feedback.error.measurementInput') ,
        required: props.isRequiredDimension('weight'),
        unit: 'g',
        unitOptions: weightUnitOptions,
      },
      {
        type: 'measurement',
        key: 'length',
        label: t('common:content.label.length'),
        errorMessage: t('item:feedback.error.measurementInput') ,
        required: props.isRequiredDimension('length'),
        unit: 'm',
        unitOptions: lengthUnitOptions,
      },
      {
        type: 'measurement',
        key: 'width',
        label: t('common:content.label.width'),
        errorMessage: t('item:feedback.error.measurementInput') ,
        required: props.isRequiredDimension('width'),
        unit: 'm',
        unitOptions: lengthUnitOptions,
      },
      {
        type: 'measurement',
        key: 'height',
        label: t('common:content.label.height'),
        errorMessage: t('item:feedback.error.measurementInput') ,
        required: props.isRequiredDimension('height'),
        unit: 'm',
        unitOptions: lengthUnitOptions,
      },
      {
        type: 'measurement',
        key: 'volume',
        label: t('common:content.label.volume'),
        errorMessage: t('item:feedback.error.measurementInput') ,
        required: props.isRequiredDimension('volume'),
        unit: 'm³',
        unitOptions: volumeUnitOptions,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'purchasedPartFormFields') {
    return [
      {
        type: 'select-search',
        key: 'supplier',
        label: t('common:content.label.supplier'),
        autocompleteField: 'suppliers',
        onAddOption:
          (canCreate(userPermissions, PERMISSIONS.SUPPLIER.SUPPLIER) || isPrivileged(user)) &&
          props.onAddSupplierOption,
        required: true,
        validation: true,
      },
      {
        type: 'select-search',
        key: 'countryOfOrigin',
        label: t('common:content.label.countryOfOrigin'),
        autocompleteField: 'countries',
        required: false,
        validation: false,
      },
      {
        type: 'text',
        key: 'minimumOrder',
        label: t('common:content.label.minimumOrder'),
      },
      {
        type: 'timedelta',
        key: 'leadTime',
        label: t('common:content.label.leadTime'),
        xl: 6,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'wareHouseFormFields') {
    return [
      /* {
        type: 'select-search',
        key: 'stockLocation',
        label: t('common:stockLocation'),
        options: [
          { value: 'A40', label: 'A40' },
          { value: 'A60', label: 'A60' },
        ],
        addButton: addButton,
      }, */
    ] as FormFieldType[]
  }
  if (props.name === 'categoryList') {
    return [
      {
        category: 'ASSEMBLY',
        label: 'content.label.assembly',
      },
      {
        category: 'CASTING',
        label: 'content.label.casting',
      },
      {
        category: 'CNCMACHINING',
        label: 'content.label.cncMachining',
      },
      {
        category: 'JOINING',
        label: 'content.label.joining',
      },
      {
        category: 'PCB',
        label: 'content.label.pcb',
      },
      {
        category: 'POSTPROCESSING',
        label: 'content.label.postProcessing',
      },
      {
        category: 'PRINTING3D',
        label: 'content.label.additiveManufacturing',
      },
      {
        category: 'QUALITYCONTROL',
        label: 'content.label.qualityControl',
      },
      {
        category: 'RAWMATERIAL',
        label: 'content.label.rawMaterial',
      },
      {
        category: 'SHEETMETAL',
        label: 'content.label.sheetMetal',
      },
      {
        category: 'STANDARDCOMPONENT',
        label: 'content.label.standardComponent',
      },
      {
        category: 'CUSTOMPROCESS',
        label: 'content.label.customProcess',
      },
    ] as any[]
  }

  if (props.name === 'sharepointFormFields') {
    const sharepointProps = props as SharepointFormFieldsKwargs
    return [
      {
        type: 'custom',
        key: 'articlesList',
        label: t('common:content.label.article'),
        CustomComponent: sharepointProps.ArticlesListComponent,
        xs: 6,
        sm: 6,
        md: 6,
        lg: 6,
        xl: 6,
      },
      {
        type: 'custom',
        key: 'articlesDescription',
        label: '',
        CustomComponent: sharepointProps.ArticlesDescriptionComponent,
        xs: 6,
        sm: 6,
        md: 6,
        lg: 6,
        xl: 6,
      },
      {
        type: 'custom',
        key: 'sharepointDuration',
        label: t('sharepoint:content.label.sharepointDuration'),
        CustomComponent: sharepointProps.DurationInputComponent,
        xs: 6,
        sm: 6,
        md: 6,
        lg: 6,
        xl: 6,
      },
      {
        type: 'custom',
        key: 'durationDescription',
        label: '',
        CustomComponent: sharepointProps.DurationDescriptionComponent,
        xs: 6,
        sm: 6,
        md: 6,
        lg: 6,
        xl: 6,
      },
      {
        type: 'select-search',
        key: 'feedbackForm',
        label: t('sharepoint:content.label.feedbackForm'),
        options: sharepointProps.feedbackFormOptions,
        onAddOption: sharepointProps.onAddFeedbackFormOption,
        required: false,
        validation: false,
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'editSharepointFormFields') {
    const editSharepointProps = props as EditSharepointFormFieldsKwargs
    return [
      {
        type: 'custom',
        key: 'articlesList',
        label: t('sharepoint:content.label.sharedArticles'),
        CustomComponent: editSharepointProps.ArticlesListComponent,
        xs: 8,
        sm: 8,
        md: 8,
        lg: 8,
        xl: 8,
      },
      {
        type: 'custom',
        key: 'articlesDescription',
        CustomComponent: editSharepointProps.ArticlesDescriptionComponent,
        xs: 4,
        sm: 4,
        md: 4,
        lg: 4,
        xl: 4,
      },
      {
        type: 'custom',
        key: 'remainingSharepointDuration',
        CustomComponent: editSharepointProps.RemainingDurationInputComponent,
        xs: 8,
        sm: 8,
        md: 8,
        lg: 8,
        xl: 8,
      },
      {
        type: 'custom',
        key: 'durationDescription',
        CustomComponent: editSharepointProps.DurationDescriptionComponent,
        xs: 4,
        sm: 4,
        md: 4,
        lg: 4,
        xl: 4,
      },
    ] as FormFieldType[]
  }

  return [] as FormFieldType[]
}
